import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BrandSettingsData, ReportStyleSettings, AccountManagerData } from './types';
import { WaterRestrictionPolicyCard } from '../WaterRestrictionPolicyCard';

interface ReportHeaderProps {
  report: any;
  jobSite: any;
  brandSettings: BrandSettingsData;
  styleSettings: ReportStyleSettings;
  formatDate: (dateString: string) => string;
  formatMonth: (monthString: string) => string;
  accountManager?: AccountManagerData | null;
  /** When provided, renders the on-screen water-restriction policy card below the
   *  property info table (viewer only; the card is hidden in print via CSS). */
  jobSiteId?: string;
}

export function ReportHeader({
  report,
  jobSite,
  brandSettings,
  styleSettings,
  formatDate,
  formatMonth,
  accountManager,
  jobSiteId,
}: ReportHeaderProps) {
  const { t } = useTranslation('inspection');
  return (
    <div className="report-header-block">
      <div
        className="report-banner flex items-center justify-between px-5 py-3"
        style={{ backgroundColor: styleSettings.header_color, color: styleSettings.header_text_color }}
      >
        <div className="flex items-center gap-3">
          {brandSettings.logo_url ? (
            <img
              src={brandSettings.logo_url}
              alt={brandSettings.company_name || t('report.companyLogoAlt')}
              className="h-9 w-auto object-contain"
              style={{ maxWidth: '140px' }}
            />
          ) : (
            <CheckCircle size={24} />
          )}
          <div className="leading-tight">
            <h1 className="text-lg font-bold tracking-tight">{t('report.headerTitle')}</h1>
            {brandSettings.company_name && (
              <p className="text-xs opacity-80">{brandSettings.company_name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-right">
          <span className="text-xs opacity-80">{formatDate(report.created_at)}</span>
          {report.status ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-white text-xs font-semibold rounded"
              style={{ backgroundColor: report.status.color }}
            >
              {report.status.status_type === 'rejected' ? <AlertCircle size={12} /> : <CheckCircle size={12} />}
              {report.status.label}
            </span>
          ) : report.is_draft ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500 text-white text-xs font-semibold rounded">
              <AlertCircle size={12} />
              {t('report.draft')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded">
              <CheckCircle size={12} />
              {t('report.submitted')}
            </span>
          )}
        </div>
      </div>

      <div className="report-info-block px-5 py-4" style={{ backgroundColor: styleSettings.body_background_color, borderBottom: `1px solid ${styleSettings.border_color}` }}>
        <table className="w-full report-info-table">
          <tbody>
            <tr>
              <td className="report-info-label">{t('report.labelProperty')}</td>
              <td className="report-info-value font-semibold">{report.job_site_name}</td>
              <td className="report-info-label">{t('report.labelReportPeriod')}</td>
              <td className="report-info-value">{formatMonth(report.month)}</td>
            </tr>
            {jobSite?.address && (
              <tr>
                <td className="report-info-label">{t('report.labelAddress')}</td>
                <td className="report-info-value" colSpan={3}>{jobSite.address}</td>
              </tr>
            )}
            <tr>
              {jobSite?.contact_name && (
                <>
                  <td className="report-info-label">{t('report.labelContact')}</td>
                  <td className="report-info-value">{jobSite.contact_name}</td>
                </>
              )}
              {!jobSite?.contact_name && (
                <>
                  <td className="report-info-label">{t('report.labelTechnicians')}</td>
                  <td className="report-info-value">{report.technicians?.join(', ')}</td>
                </>
              )}
              {jobSite?.contact_phone && (
                <>
                  <td className="report-info-label">{t('report.labelPhone')}</td>
                  <td className="report-info-value">{jobSite.contact_phone}</td>
                </>
              )}
              {!jobSite?.contact_phone && jobSite?.contact_email && (
                <>
                  <td className="report-info-label">{t('report.labelEmail')}</td>
                  <td className="report-info-value">{jobSite.contact_email}</td>
                </>
              )}
              {!jobSite?.contact_phone && !jobSite?.contact_email && !jobSite?.contact_name && (
                <>
                  <td></td>
                  <td></td>
                </>
              )}
            </tr>
            {jobSite?.contact_name && (
              <tr>
                <td className="report-info-label">{t('report.labelTechnicians')}</td>
                <td className="report-info-value">{report.technicians?.join(', ')}</td>
                {jobSite?.contact_email && (
                  <>
                    <td className="report-info-label">{t('report.labelEmail')}</td>
                    <td className="report-info-value">{jobSite.contact_email}</td>
                  </>
                )}
                {!jobSite?.contact_email && jobSite?.contact_phone && (
                  <>
                    <td></td>
                    <td></td>
                  </>
                )}
                {!jobSite?.contact_email && !jobSite?.contact_phone && (
                  <>
                    <td className="report-info-label">{t('report.labelPhone')}</td>
                    <td className="report-info-value">--</td>
                  </>
                )}
              </tr>
            )}
            {accountManager && accountManager.name && (
              <tr>
                <td className="report-info-label">{t('report.labelAccountManager')}</td>
                <td className="report-info-value">{accountManager.name}</td>
                {accountManager.email && (
                  <>
                    <td className="report-info-label">{t('report.labelAmEmail')}</td>
                    <td className="report-info-value">{accountManager.email}</td>
                  </>
                )}
                {!accountManager.email && accountManager.phone && (
                  <>
                    <td className="report-info-label">{t('report.labelAmPhone')}</td>
                    <td className="report-info-value">{accountManager.phone}</td>
                  </>
                )}
                {!accountManager.email && !accountManager.phone && (
                  <>
                    <td></td>
                    <td></td>
                  </>
                )}
              </tr>
            )}
          </tbody>
        </table>
        {jobSiteId && (
          <WaterRestrictionPolicyCard jobSiteId={jobSiteId} className="mt-4 no-print" />
        )}
      </div>
    </div>
  );
}
